import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { auth, db } from "../firebase";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
} from "firebase/firestore";

const ADMIN_EMAIL = "usanamysalesteam@gmail.com";
const provider = new GoogleAuthProvider();

// ── All original helper functions — untouched ──────────────────────

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function excelDateToText(value) {
  if (!value) return "";

  // ── Helper: format a Date object as YYYY-MM-DD ──
  // xlsx's cellDates option produces dates that can be at either UTC midnight
  // OR local midnight, depending on the xlsx version + the user's timezone.
  // To handle BOTH cases reliably:
  //   1. If the Date's UTC time is exactly midnight (xx:00:00.000Z), use UTC components
  //   2. Otherwise, use local components (it was created from local midnight)
  const toDateString = (d) => {
    // Check if the Date represents UTC midnight (typical for Excel date cells
    // when xlsx normalizes to UTC — happens in some xlsx versions/browsers).
    const isUTCMidnight = d.getUTCHours() === 0 &&
                          d.getUTCMinutes() === 0 &&
                          d.getUTCSeconds() === 0;

    if (isUTCMidnight) {
      // Use UTC components — calendar date stored as UTC midnight
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const day = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }

    // Use local components — calendar date stored as local midnight
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Native Date object (xlsx returns these for date cells)
  if (value instanceof Date) return toDateString(value);

  // Excel numeric date → parse via SSF (this path is timezone-safe)
  if (typeof value === "number") {
    const date = XLSX.SSF.parse_date_code(value);
    if (!date) return "";
    const month = String(date.m).padStart(2, "0");
    const day = String(date.d).padStart(2, "0");
    return `${date.y}-${month}-${day}`;
  }

  // String input — defensive parsing
  // Strip parenthesised annotations like "(Cycle 3)" before parsing
  let cleaned = String(value).trim().replace(/\s*\([^)]*\)\s*/g, " ").trim();
  if (!cleaned) return "";

  // Try to parse the cleaned string into a real Date
  const parsed = new Date(cleaned);
  if (!isNaN(parsed.getTime())) {
    return toDateString(parsed);
  }

  // If parsing failed, store the cleaned string as-is (better than nothing)
  return cleaned;
}

// ── Passthrough: stores whatever the admin typed in the Excel cell, as-is ──
// Used for expiry date columns where we don't want any timezone math —
// the admin's typed text is gospel. Whatever shows up in Excel goes
// straight to Firestore and straight to the dashboard.
function cellAsText(value) {
  if (value === null || value === undefined) return "";

  // Date object (when xlsx parses a date cell) → format using UTC components
  // shifted by 12h to immunise against timezone drift
  if (value instanceof Date) {
    const adjusted = new Date(value.getTime() + 12 * 60 * 60 * 1000);
    const y = adjusted.getUTCFullYear();
    const m = String(adjusted.getUTCMonth() + 1).padStart(2, "0");
    const d = String(adjusted.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  // Everything else — number, string — just stringify it as-is
  return String(value).trim();
}

async function commitBatchInChunks(operations) {
  const chunkSize = 450;
  for (let i = 0; i < operations.length; i += chunkSize) {
    const batch = writeBatch(db);
    const chunk = operations.slice(i, i + chunkSize);
    chunk.forEach((operation) => operation(batch));
    await batch.commit();
  }
}

// ── Step indicator sub-component ──────────────────────────────────

function Step({ label, state }) {
  return (
    <div className={`upload-step upload-step--${state}`}>
      <div className="upload-step-icon">
        {state === "done" && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {state === "active" && <span className="upload-step-spinner" />}
        {state === "idle"   && <span className="upload-step-dot" />}
        {state === "error"  && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        )}
      </div>
      <span>{label}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────

export default function AdminUpload() {
  const [adminUser, setAdminUser]           = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isUploading, setIsUploading]       = useState(false);
  const [dragOver, setDragOver]             = useState(false);
  const [uploadResult, setUploadResult]     = useState(null);
  const [error, setError]                   = useState("");
  const [pendingFile, setPendingFile]       = useState(null);

  const [steps, setSteps] = useState({
    read: "idle", clearU: "idle", clearD: "idle", uploadU: "idle", uploadD: "idle",
  });

  const setStep = (key, state) => setSteps((p) => ({ ...p, [key]: state }));

  // ── Firebase auth listener (unchanged logic) ──
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && normalizeEmail(user.email) === normalizeEmail(ADMIN_EMAIL)) {
        setAdminUser(user);
      } else {
        setAdminUser(null);
      }
      setCheckingAccess(false);
    });
    return () => unsubscribe();
  }, []);

  const handleAdminLogin = async () => {
    try {
      setCheckingAccess(true);
      setError("");
      const result = await signInWithPopup(auth, provider);
      if (normalizeEmail(result.user.email) !== normalizeEmail(ADMIN_EMAIL)) {
        setError("Access denied. This Gmail is not the admin account.");
        await signOut(auth);
        setAdminUser(null);
        return;
      }
      setAdminUser(result.user);
    } catch (err) {
      console.error(err);
      setError("Admin login failed. Please try again.");
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAdminLogout = async () => {
    await signOut(auth);
    setAdminUser(null);
    setUploadResult(null);
    setError("");
  };

  // ── Firebase write helpers (logic unchanged) ──
  const clearCollection = async (collectionName) => {
  const snapshot = await getDocs(collection(db, collectionName));
  const deleteOperations = snapshot.docs.map((document) => (batch) => {
    batch.delete(doc(db, collectionName, document.id));
  });
  await commitBatchInChunks(deleteOperations);
};

  const uploadUsers = async (rows) => {
    const operations = [];
    let skipped = 0;
    rows.forEach((row) => {
      const gmail = normalizeEmail(row.gmail);
      if (!gmail) {
        skipped++; // row has no gmail — cannot create a user
        return;
      }
      const userRef = doc(db, "users", gmail);
      operations.push((batch) => {
        batch.set(userRef, {
          usanaId: normalizeText(row.usanaId),
          name: normalizeText(row.name),
          rank: normalizeText(row.rank),
          gmail,
          carryForwardPoints: normalizeNumber(row.carryForwardPoints),
          pacesetterCreated: normalizeNumber(row.pacesetterCreated),
          baseCp: normalizeNumber(row.baseCp),
          accumulatedCp: normalizeNumber(row.accumulatedCp),
          reportDate: excelDateToText(row.reportDate),
        });
      });
    });
    await commitBatchInChunks(operations);
    return { uploaded: operations.length, skipped };
  };

  const uploadDirectors = async (rows) => {
    const operations = [];
    let skipped = 0;
    rows.forEach((row) => {
      const sponsorUsanaId  = normalizeText(row.sponsorUsanaId);
      const directorUsanaId = normalizeText(row.directorUsanaId);
      if (!sponsorUsanaId || !directorUsanaId) {
        skipped++; // row missing sponsor or director ID — cannot link
        return;
      }
      const directorId  = `${sponsorUsanaId}_${directorUsanaId}`;
      const directorRef = doc(db, "directors", directorId);

      operations.push((batch) => {
        // Normalize status — case-insensitive match on "qualified"
        const rawStatus = normalizeText(row.qualificationStatus).toLowerCase();
        const qualificationStatus = rawStatus === "qualified" ? "qualified" : "";

        batch.set(directorRef, {
          sponsorUsanaId,
          sponsorName: normalizeText(row.sponsorName),
          directorUsanaId,
          directorName: normalizeText(row.directorName),
          newRank: normalizeText(row.newRank),
          newRankDate: excelDateToText(row.newRankDate),
          comQuaExpiryDate: cellAsText(row.comQuaExpiryDate),
          fiftyCpExpiryDate: cellAsText(row.fiftyCpExpiryDate),
          qualificationStatus,
        });
      });
    });
    await commitBatchInChunks(operations);
    return { uploaded: operations.length, skipped };
  };

  // ── Core upload orchestrator ──
  const processFile = async (file) => {
    if (!file) return;
    try {
      setIsUploading(true);
      setUploadResult(null);
      setError("");
      setSteps({ read: "active", clearU: "idle", clearD: "idle", uploadU: "idle", uploadD: "idle" });

      const data     = await file.arrayBuffer();
      const workbook = XLSX.read(data, { cellDates: false, cellText: true });

      const userSheet     = workbook.Sheets["User Table"];
      const directorSheet = workbook.Sheets["Director Table"];

      if (!userSheet) {
        setError('Sheet "User Table" not found. Please check your file.');
        setStep("read", "error");
        return;
      }
      if (!directorSheet) {
        setError('Sheet "Director Table" not found. Please check your file.');
        setStep("read", "error");
        return;
      }

      const userRows     = XLSX.utils.sheet_to_json(userSheet, { raw: false });
      const directorRows = XLSX.utils.sheet_to_json(directorSheet, { raw: false });
      setStep("read", "done");

      setStep("clearU", "active");
      await clearCollection("users");
      setStep("clearU", "done");

      setStep("clearD", "active");
      await clearCollection("directors");
      setStep("clearD", "done");

      setStep("uploadU", "active");
      const userResult = await uploadUsers(userRows);
      setStep("uploadU", "done");

      setStep("uploadD", "active");
      const directorResult = await uploadDirectors(directorRows);
      setStep("uploadD", "done");

      setUploadResult({
        users: userResult.uploaded,
        usersSkipped: userResult.skipped,
        directors: directorResult.uploaded,
        directorsSkipped: directorResult.skipped,
      });
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please check the browser console for details.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (file) {
      setError("");
      setPendingFile(file); // stage file — wait for confirmation
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) {
      setError("");
      setPendingFile(file); // stage file — wait for confirmation
    } else {
      setError("Please drop a valid .xlsx or .xls file.");
    }
  };

  // Called when the admin confirms in the dialog
  const handleConfirmUpload = async () => {
    const file = pendingFile;
    setPendingFile(null);
    await processFile(file);
  };

  // Called when the admin cancels the dialog
  const handleCancelUpload = () => {
    setPendingFile(null);
  };

  // ── Render: checking access ──
  if (checkingAccess) {
    return (
      <main className="admin-page">
        <div className="admin-checking">
          <span className="spinner-lg" />
          <p>Verifying admin access…</p>
        </div>
      </main>
    );
  }

  // ── Render: not logged in ──
  if (!adminUser) {
    return (
      <main className="admin-page">
        <section className="admin-card admin-card--login">
          <div className="admin-lock-icon">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <rect x="3" y="11" width="20" height="13" rx="3" stroke="currentColor" strokeWidth="1.7"/>
              <path d="M8 11V8.5a5 5 0 0110 0V11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
              <circle cx="13" cy="17.5" r="1.8" fill="currentColor"/>
            </svg>
          </div>
          <h1 className="admin-title">Admin Access</h1>
          <p className="admin-subtitle">Sign in with the admin Gmail account to manage data.</p>

          {error && (
            <div className="admin-error" role="alert">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M7.5 4.5v4M7.5 10.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              {error}
            </div>
          )}

          <button className="google-btn" onClick={handleAdminLogin}>
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google"
              width="18"
              height="18"
            />
            <span>Sign in as Admin</span>
          </button>

          <p className="login-note">Admin account: {ADMIN_EMAIL}</p>
        </section>
      </main>
    );
  }

  // ── Render: admin dashboard ──
  return (
    <main className="admin-page">
      <section className="admin-card">

        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-left">
            <div className="admin-avatar">
              {adminUser.email?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="admin-title">Data Upload</h1>
              <p className="admin-signed-in">{adminUser.email}</p>
            </div>
          </div>
          <button className="admin-logout-btn" onClick={handleAdminLogout}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <path d="M9.5 10.5l3-3-3-3M12.5 7.5H5.5M5.5 2H3a1 1 0 00-1 1v9a1 1 0 001 1h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Logout
          </button>
        </div>

        <div className="admin-divider" />

        {/* Requirements */}
        <div className="admin-requirements">
          <div className="admin-req-item">
            <span className="admin-req-label">Required sheets</span>
            <span><code>User Table</code> and <code>Director Table</code></span>
          </div>
          <div className="admin-req-item">
            <span className="admin-req-label">User Table must include</span>
            <span><code>reportDate</code> column</span>
          </div>
          <div className="admin-req-item admin-req-item--warn">
            <span className="admin-req-label">Warning</span>
            <span>All existing data will be replaced on upload</span>
          </div>
        </div>

        <div className="admin-divider" />

        {/* Drop zone */}
        <div
          className={`admin-dropzone ${dragOver ? "drag-over" : ""} ${isUploading ? "is-uploading" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="admin-dz-icon">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M14 20V10M14 10l-4 4M14 10l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 20v1.5A2.5 2.5 0 007.5 24h13a2.5 2.5 0 002.5-2.5V20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
            </svg>
          </div>
          <p className="admin-dz-title">
            {isUploading ? "Uploading…" : "Drop your Excel file here"}
          </p>
          <p className="admin-dz-sub">
            {isUploading ? "Please wait, do not close the tab" : "or click the button below to browse"}
          </p>
          <label className={`admin-file-btn ${isUploading ? "disabled" : ""}`}>
            {isUploading ? "Processing…" : "Choose File (.xlsx / .xls)"}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="admin-file-input"
            />
          </label>
        </div>

        {/* Upload step progress */}
        {isUploading && (
          <div className="upload-steps">
            <Step label="Reading Excel file"        state={steps.read} />
            <Step label="Clearing existing users"   state={steps.clearU} />
            <Step label="Clearing existing directors" state={steps.clearD} />
            <Step label="Uploading users"           state={steps.uploadU} />
            <Step label="Uploading directors"       state={steps.uploadD} />
          </div>
        )}

        {/* Success banner */}
        {uploadResult && !isUploading && (
          <div className="admin-success" role="status">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 10l3 3 5-5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <p className="admin-success-title">Upload complete!</p>
              <p className="admin-success-sub">
                {uploadResult.users.toLocaleString()} users · {uploadResult.directors.toLocaleString()} directors synced to Firestore
              </p>

              {(uploadResult.usersSkipped > 0 || uploadResult.directorsSkipped > 0) && (
                <div className="admin-skipped-note">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M6.5 4v3M6.5 9v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                  <span>
                    {uploadResult.usersSkipped > 0 && (
                      <>{uploadResult.usersSkipped} user row{uploadResult.usersSkipped !== 1 ? "s" : ""} skipped (missing gmail)</>
                    )}
                    {uploadResult.usersSkipped > 0 && uploadResult.directorsSkipped > 0 && " · "}
                    {uploadResult.directorsSkipped > 0 && (
                      <>{uploadResult.directorsSkipped} director row{uploadResult.directorsSkipped !== 1 ? "s" : ""} skipped (missing ID)</>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && !isUploading && (
          <div className="admin-error" role="alert">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M7.5 4.5v4M7.5 10.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

      </section>

      {/* ── Upload confirmation dialog ── */}
      {pendingFile && (
        <div className="ggd-overlay" onClick={handleCancelUpload}>
          <div className="ggd-modal admin-confirm-modal" onClick={(e) => e.stopPropagation()}>

            <div className="admin-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                <path d="M13 2L2 22h22L13 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M13 10v5M13 18v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>

            <h2 className="admin-confirm-title">Replace all data?</h2>
            <p className="admin-confirm-desc">
              This will <strong>permanently delete all existing users and directors</strong>,
              then upload the new data from your file. This cannot be undone.
            </p>

            <div className="admin-confirm-file">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 1.5h6L14.5 6v10a1 1 0 01-1 1h-9a1 1 0 01-1-1V2.5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M10 1.5V6h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
              <span className="admin-confirm-filename">{pendingFile.name}</span>
            </div>

            <div className="admin-confirm-actions">
              <button className="admin-confirm-cancel" onClick={handleCancelUpload}>
                Cancel
              </button>
              <button className="admin-confirm-proceed" onClick={handleConfirmUpload}>
                Yes, replace all data
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}